import React, { useMemo, useState, useEffect } from 'react';
import { RiBuilding2Line, RiArrowDownSLine, RiCheckLine, RiLoader4Line, RiSearchLine, RiAddLine, RiEditLine } from '@remixicon/react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from '@components/ui/Drawer';
import { cn } from '@components/ui/utils';
import { COUNTRY_CODES, FlagImg } from '@/utils/countryCode';
import { useFbos } from '../services/fboService';

/**
 * Normalise an Aviapages FBO record into the compact ref we persist.
 * Keeps id + name (required) plus address/phone so the trip-sheet render and a
 * later re-map have what they need.
 */
const toFboRef = (fbo) => ({
  id: fbo.id,
  name: fbo.name || '',
  address: fbo.address || '',
  phone: fbo.phone || '',
});

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Drawer title with the airport ICAO rendered as a styled subtitle below it.
const HeaderTitle = ({ title, subtitle }) => (
  <div className="flex flex-col text-left">
    <span>{title}</span>
    {subtitle && (
      <span
        className="font-medium mt-0.5"
        style={{ fontSize: '16px', lineHeight: '24px', letterSpacing: '-0.011em', color: '#5C5C5C' }}
      >
        {subtitle}
      </span>
    )}
  </div>
);

/**
 * AddOtherFboDrawer
 * Side drawer for adding/editing a manually-entered FBO (not in the directory).
 * Mirrors the AddCrew dialog content layout — Name (required), Address, Phone
 * (with country code). Layered above the selector drawer via z-[1200].
 */
const AddOtherFboDrawer = ({ isOpen, onClose, onAdd, editingFbo, subtitle, side }) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [codeDropdownOpen, setCodeDropdownOpen] = useState(false);

  const isEditing = !!editingFbo;

  useEffect(() => {
    if (!isOpen) return;
    if (editingFbo) {
      setName(editingFbo.name || '');
      setAddress(editingFbo.address || '');
      const match = editingFbo.phone?.match(/^(\+\d{1,3})\s*(.*)/);
      if (match) {
        setCountryCode(match[1]);
        setPhone(match[2]);
      } else {
        setCountryCode('+1');
        setPhone(editingFbo.phone || '');
      }
    } else {
      setName('');
      setAddress('');
      setCountryCode('+1');
      setPhone('');
    }
  }, [isOpen, editingFbo]);

  const isFormValid = name.trim();

  const handleSubmit = () => {
    if (!isFormValid) return;
    const hasPhone = phone.replace(/\D/g, '').length > 0;
    onAdd({
      id: 'other',
      name: name.trim(),
      address: address.trim(),
      phone: hasPhone ? `${countryCode} ${phone}` : '',
    });
  };

  const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0];

  return (
    <Drawer open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent
        overlayClassName="z-[1200]"
        className="sm:max-w-md !h-[100dvh] flex flex-col pb-[env(safe-area-inset-bottom)] z-[1200]"
      >
        <DrawerHeader className="shrink-0">
          <DrawerTitle>
            <HeaderTitle
              title={isEditing ? 'Edit Other FBO' : 'Add Other FBO'}
              subtitle={subtitle}
            />
          </DrawerTitle>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto pt-3 pb-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              FBO Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="FBO name"
              className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter address"
              className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setCodeDropdownOpen(!codeDropdownOpen)}
                  className="flex items-center gap-1.5 border border-gray-200 rounded-xl px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors min-w-[90px]"
                >
                  <FlagImg iso={selectedCountry.iso} />
                  <span className="text-gray-700">{selectedCountry.code}</span>
                  <RiArrowDownSLine className="w-3.5 h-3.5 text-gray-400" />
                </button>
                {codeDropdownOpen && (
                  <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto w-36">
                    {COUNTRY_CODES.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => { setCountryCode(c.code); setCodeDropdownOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${c.code === countryCode ? 'bg-primary-50 text-primary-600' : 'text-gray-700'}`}
                      >
                        <FlagImg iso={c.iso} />
                        <span>{c.code}</span>
                        <span className="text-gray-400 text-xs">{c.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder="(555) 000-0000"
                className="flex-1 text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-primary-400 transition-colors"
              />
            </div>
          </div>
        </div>

        <DrawerFooter className="shrink-0">
          <button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={`w-full flex items-center justify-center py-3 text-sm font-medium rounded-full border transition-colors mb-3 ${
              isFormValid
                ? 'bg-primary-50 text-primary-500 border-primary-50 hover:bg-primary-100'
                : 'bg-gray-100 text-gray-400 border-gray-100 cursor-not-allowed'
            }`}
          >
            <RiAddLine className="w-4 h-4 mr-2" />
            {isEditing ? 'Save Changes' : (side ? `Add ${side} FBO` : 'Add FBO')}
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

/**
 * FboSelectDrawer
 * Side drawer listing an airport's FBOs (name + phone). Lets the broker pick
 * one — or open the Add Other FBO drawer for an unlisted FBO. Fetches lazily
 * (only when open) and is cached per ICAO so re-opening never re-hits the API.
 */
export const FboSelectDrawer = ({ isOpen, onClose, onReopen, icao, value, onSelect, title, subtitle, side }) => {
  const { data: fbos = [], isLoading, isError } = useFbos(icao, isOpen);
  const [query, setQuery] = useState('');
  const [addOtherOpen, setAddOtherOpen] = useState(false);
  // null = add a new "Other"; a value = edit the existing one (pre-fills the form).
  const [addOtherEditing, setAddOtherEditing] = useState(null);

  const openAddOther = (existing) => {
    setAddOtherEditing(existing || null);
    onClose();              // close the selector while Add Other is on screen
    setAddOtherOpen(true);
  };

  const closeAddOther = () => {
    setAddOtherOpen(false);
    if (onReopen) onReopen(); // X / dismiss → reopen the selector behind it
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return fbos;
    return fbos.filter((f) =>
      (f.name || '').toLowerCase().includes(q) ||
      (f.company?.name || '').toLowerCase().includes(q)
    );
  }, [fbos, query]);

  const pick = (ref) => {
    onSelect(ref);
    onClose();
    setQuery('');
  };

  const isOther = value?.id === 'other';

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(o) => !o && onClose()}>
        <DrawerContent
          overlayClassName="z-[1100]"
          className="sm:max-w-md !h-[100dvh] flex flex-col pb-[env(safe-area-inset-bottom)] z-[1100]"
        >
          <DrawerHeader className="shrink-0">
            <DrawerTitle>
              <HeaderTitle title={title || 'Select FBO'} subtitle={subtitle} />
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto pt-3 pb-5 space-y-4">
            {/* Search */}
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FBOs"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-primary-500"
              />
            </div>

            {/* List — Name + phone only */}
            <div className="space-y-2">
              {isLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                  <RiLoader4Line className="w-4 h-4 animate-spin text-primary-600" />
                  Loading FBOs…
                </div>
              ) : isError ? (
                <p className="py-6 text-center text-sm text-red-600">Couldn't load FBOs for this airport.</p>
              ) : filtered.length === 0 ? (
                <p className="py-6 text-center text-sm text-gray-500">No FBOs found. Add one below.</p>
              ) : (
                filtered.map((fbo) => {
                  const selected = value?.id === fbo.id;
                  return (
                    <button
                      key={fbo.id}
                      type="button"
                      onClick={() => pick(toFboRef(fbo))}
                      className={cn(
                        'w-full text-left p-3 rounded-xl border transition-colors',
                        selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{fbo.name}</p>
                          {fbo.phone && <p className="text-xs text-gray-500 mt-0.5">{fbo.phone}</p>}
                        </div>
                        {selected && <RiCheckLine className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* When an "Other" FBO is already selected, surface an editable card
                in the list area; the Add Other button stays in the footer. */}
            {isOther && (
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between gap-2 p-3 rounded-xl border border-primary-200 bg-primary-50">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{value.name}</p>
                    {value.phone && <p className="text-xs text-gray-500 mt-0.5">{value.phone}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => openAddOther(value)}
                    aria-label="Edit other FBO"
                    className="flex-shrink-0 p-2 rounded-full text-primary-600 hover:bg-primary-100 transition-colors"
                  >
                    <RiEditLine className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <DrawerFooter className="shrink-0">
            <button
              type="button"
              onClick={() => openAddOther(null)}
              className="w-full flex items-center justify-center gap-1.5 py-3 rounded-full bg-primary-50 text-primary-500 text-sm font-medium border border-primary-50 hover:bg-primary-100 transition-colors mb-3"
            >
              <RiAddLine className="w-4 h-4" />
              Add Other FBO
            </button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AddOtherFboDrawer
        isOpen={addOtherOpen}
        editingFbo={addOtherEditing}
        subtitle={subtitle}
        side={side}
        onClose={closeAddOther}
        onAdd={(ref) => {
          setAddOtherOpen(false);
          pick(ref); // selects + ensures the selector stays closed
        }}
      />
    </>
  );
};

/**
 * FboInput
 * Clickable, dropdown-styled field (icon + value + chevron) mirroring the
 * Generate-Quote location input. Opens the FBO selector side drawer on click.
 *
 * @param {Object} props.value - selected FBO ref { id, name } | null
 * @param {Function} props.onChange - (fboRef|null) => void
 * @param {string} props.icao - airport ICAO whose FBO list to show
 * @param {string} props.label
 * @param {string} [props.error]
 */
const FboInput = ({ value, onChange, icao, label, error = '', icon: Icon = RiBuilding2Line }) => {
  const [open, setOpen] = useState(false);

  // Derive "Select Departure/Arrival FBO" from the field label.
  const side = /departure/i.test(label || '') ? 'Departure' : /arrival/i.test(label || '') ? 'Arrival' : '';
  const modalTitle = side ? `Select ${side} FBO` : 'Select FBO';

  return (
    <div className="w-full">
      {label && (
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-gray-700">{label}</label>
          {icao && (
            <span
              className="text-xs font-medium text-right uppercase"
              style={{ lineHeight: '16px', letterSpacing: '0.04em', color: '#A3A3A3' }}
            >
              {icao}
            </span>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'relative w-full text-left pl-9 pr-9 py-2.5 border rounded-lg text-sm transition-all',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400',
          value ? 'text-gray-700' : 'text-gray-400'
        )}
      >
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-700" />
        <span className="block truncate">{value?.name || 'Select FBO'}</span>
        <RiArrowDownSLine className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
      </button>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      <FboSelectDrawer
        isOpen={open}
        onClose={() => setOpen(false)}
        onReopen={() => setOpen(true)}
        icao={icao}
        value={value}
        onSelect={onChange}
        title={modalTitle}
        subtitle={icao}
        side={side}
      />
    </div>
  );
};

export default FboInput;
